import L from "leaflet";

import { drawText, drawLine } from "@/utils/Canvas";

import DmsCoordinates from "dms-conversion";

const MAP_GRATICULE_BORDER = 50;
const BORDER_LINE_LENGTH = 10;
const DRAW_TEXT_OFFSET = 35;
const DRAW_LINE_OFFSET = 40;
const CONTENT_LINE_LENGTH = 20;

export class MapGraticuleDrawer {
  /**
   *
   * @param {HTMLCanvasElement} mapScreenShotCanvas
   * @param {L.Map} map
   */
  static draw(mapScreenShotCanvas, map) {
    if (!(map instanceof L.Map)) return;

    const mapGraticuleCanvas = this.createMapGraticuleCanvas(
      mapScreenShotCanvas
    );

    const { lngPoints, latPoints } = this.calculateGraticulePoints(
      map,
      mapScreenShotCanvas
    );

    this.drawGraticulePoints(lngPoints, latPoints, mapGraticuleCanvas, map);

    return mapGraticuleCanvas;
  }

  /**
   *
   * @param {HTMLCanvasElement} mapScreenShotCanvas
   */
  static createMapGraticuleCanvas(mapScreenShotCanvas) {
    const mapGraticuleCanvas = document.createElement("canvas");
    mapGraticuleCanvas.width =
      mapScreenShotCanvas.width + MAP_GRATICULE_BORDER * 2;
    mapGraticuleCanvas.height =
      mapScreenShotCanvas.height + MAP_GRATICULE_BORDER * 2;

    const ctx = mapGraticuleCanvas.getContext("2d");

    // Draw white background
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, mapGraticuleCanvas.width, mapGraticuleCanvas.height);

    // Draw out border
    ctx.beginPath();
    ctx.lineWidth = "5";
    ctx.strokeStyle = "black";
    ctx.rect(0, 0, mapGraticuleCanvas.width, mapGraticuleCanvas.height);
    ctx.stroke();

    // Draw in border
    ctx.beginPath();
    ctx.lineWidth = "2";
    ctx.strokeStyle = "black";
    ctx.rect(
      MAP_GRATICULE_BORDER,
      MAP_GRATICULE_BORDER,
      mapScreenShotCanvas.width,
      mapScreenShotCanvas.height
    );
    ctx.stroke();

    // Draw map image
    ctx.drawImage(
      mapScreenShotCanvas,
      MAP_GRATICULE_BORDER,
      MAP_GRATICULE_BORDER
    );

    return mapGraticuleCanvas;
  }

  /**
   *
   * @param {L.Map} map
   * @param {HTMLCanvasElement} mapScreenShotCanvas
   */
  static calculateGraticulePoints(map, mapScreenShotCanvas) {
    const bounds = map.getBounds();
    const [w, e, s, n] = [
      bounds.getWest(),
      bounds.getEast(),
      bounds.getSouth(),
      bounds.getNorth()
    ];

    const labelTextSize = 90;

    const maxLngPoints = 10;
    const maxLatPoints = 7;

    const numberOfLngPoints = Math.min(
      maxLngPoints,
      Math.floor(mapScreenShotCanvas.height / labelTextSize)
    );
    const numberOfLatPoints = Math.min(
      maxLatPoints,
      Math.floor(mapScreenShotCanvas.width / labelTextSize)
    );

    const lngPoints = this.calculateLatLngPoints(
      w,
      e,
      numberOfLngPoints,
      "long"
    );
    const latPoints = this.calculateLatLngPoints(
      s,
      n,
      numberOfLatPoints,
      "lat"
    );

    return { lngPoints, latPoints };
  }

  /**
   *
   * @param {Number} start
   * @param {Number} end
   * @param {Number} numberOfPoints
   * @param {String} longOrLat 'long' or 'lat'
   */
  static calculateLatLngPoints(start, end, numberOfPoints, longOrLat) {
    let lineSegment;
    try {
      lineSegment = this.roundDegrees(
        (end - start) / numberOfPoints,
        longOrLat
      );
    } catch (e) {
      console.error(
        "Round Degrees Failed",
        start,
        end,
        numberOfPoints,
        longOrLat
      );
      throw e;
    }

    const firstPoint = this.findFirstPoint(start, lineSegment, longOrLat);

    const points = [];
    let currentPoint = firstPoint;

    if (isNaN(lineSegment)) {
      throw new Error("Line Segment is invalid", lineSegment);
    }

    while (currentPoint <= end) {
      points.push(currentPoint);
      currentPoint += lineSegment;
    }

    return points;
  }

  /**
   *
   * @param {Number} start
   * @param {Number} lineSegment
   * @param {String} longOrLat 'long' or 'lat'
   */
  static findFirstPoint(start, lineSegment, longOrLat) {
    let result;

    try {
      result = this.roundDegrees(start + lineSegment / 2, longOrLat);
    } catch (e) {
      console.error("Round Degrees Failed", start, lineSegment, longOrLat);
      throw e;
    }

    return result;
  }

  /**
   *
   * @param {Number} degrees
   * @param {String} longOrLat 'long' or 'lat'
   */
  static roundDegrees(degrees, longOrLat) {
    // Convert to dms
    const { d, m, s, nsew } = this.toDms(degrees, longOrLat);

    // Round
    const roundDms = [];
    roundDms[0] = d;
    roundDms[1] = m !== 0 ? this.findModNearest(m, 15, 15) : 0;
    roundDms[2] =
      roundDms[1] === 0 ? this.findModNearest(Math.floor(s), 30, 30, 60) : 0;
    roundDms[3] = nsew;

    // Convert to decimal degrees
    const dd = this.toDecimalDegrees(roundDms);

    return dd;
  }

  /**
   *
   * @param {Number} dd
   * @param {String} longOrLat 'long' or 'lat'
   */
  static toDms(dd, longOrLat) {
    const dms =
      longOrLat === "long"
        ? new DmsCoordinates(0, dd)
        : new DmsCoordinates(dd, 0);
    const [d, m, s, nsew] =
      longOrLat === "long" ? dms.dmsArrays.longitude : dms.dmsArrays.latitude;
    return { d, m, s, nsew };
  }

  /**
   *
   * @param {Number} number
   * @param {Number} m
   * @param {Number} min
   * @param {Number} max
   */
  static findModNearest(number, m, min, max) {
    let upperRound = number;
    let lowerRound = number;
    let result = number;

    for (let i = 0; i < 1000; i++) {
      if (upperRound % m === 0) break;

      upperRound++;

      if (i >= 1000) console.error("Limit archive");
    }

    if (max !== undefined) upperRound = Math.min(max, upperRound);

    for (let i = 0; i < 1000; i++) {
      if (lowerRound % m === 0) break;

      lowerRound--;

      if (i >= 1000) console.error("Limit archive");
    }

    if (min !== undefined) lowerRound = Math.max(min, lowerRound);

    if (lowerRound < 0) {
      result = upperRound;
    } else {
      result =
        Math.abs(number - lowerRound) < Math.abs(upperRound - number)
          ? lowerRound
          : upperRound;
    }

    return result;
  }

  /**
   *
   * @param {Number[]} dms
   */
  static toDecimalDegrees(dms) {
    let output = NaN;
    const degrees = Number(dms[0]);

    const minutes = typeof dms[1] !== "undefined" ? Number(dms[1]) / 60 : 0;
    const seconds = typeof dms[2] !== "undefined" ? Number(dms[2]) / 3600 : 0;
    const hemisphere = dms[3] || null;

    if (hemisphere !== null && /[SW]/i.test(hemisphere)) {
      output = -Math.abs(degrees) - minutes - seconds;
    } else {
      output = degrees + minutes + seconds;
    }

    return output;
  }

  static drawGraticulePoints(lngPoints, latPoints, canvas, map) {
    const bounds = map.getBounds();
    const [w, e, s, n] = [
      bounds.getWest(),
      bounds.getEast(),
      bounds.getSouth(),
      bounds.getNorth()
    ];

    const topBorder = lngPoints.map(p => {
      const latLng = L.latLng(n, p);
      return {
        value: map.latLngToContainerPoint(latLng),
        text: this.toDmsText(p, "long")
      };
    });

    const bottomBorder = lngPoints.map(p => {
      const latLng = L.latLng(s, p);
      return {
        value: map.latLngToContainerPoint(latLng),
        text: this.toDmsText(p, "long")
      };
    });

    const leftBorder = latPoints.map(p => {
      const latLng = L.latLng(p, w);
      return {
        value: map.latLngToContainerPoint(latLng),
        text: this.toDmsText(p, "lat")
      };
    });

    const rightBorder = latPoints.map(p => {
      const latLng = L.latLng(p, e);
      return {
        value: map.latLngToContainerPoint(latLng),
        text: this.toDmsText(p, "lat")
      };
    });

    const contentPoints = [];
    for (let i = lngPoints.length - 1; i >= 0; i--) {
      for (let j = latPoints.length - 1; j >= 0; j--) {
        contentPoints.push(
          map.latLngToContainerPoint(L.latLng(latPoints[j], lngPoints[i]))
        );
      }
    }

    // Draw borders
    this.drawBorder(canvas, "top", topBorder);
    this.drawBorder(canvas, "bottom", bottomBorder);
    this.drawBorder(canvas, "left", leftBorder);
    this.drawBorder(canvas, "right", rightBorder);

    this.drawContentPoints(canvas, contentPoints);
  }

  static toDmsText(dd, longOrLat) {
    const dms = this.toDms(dd, longOrLat);
    return `${dms.d}°${dms.m}′${Math.floor(dms.s)}″${dms.nsew}`;
  }

  static drawBorder(canvas, direction, points) {
    for (let i = 0, len = points.length; i < len; i++) {
      const point = points[i];
      const start = point.value;
      const end = this.findEndPoint(start, direction, BORDER_LINE_LENGTH);

      this.drawLine(canvas, start, end);

      let textOffset = 10;
      let textPos;
      switch (direction) {
        case "top":
          textPos = { x: end.x, y: end.y - textOffset };
          break;
        case "bottom":
          textPos = { x: end.x, y: end.y + textOffset };
          break;
        case "right":
          textPos = { x: end.x + textOffset, y: end.y };
          break;
        case "left":
          textPos = { x: end.x - textOffset, y: end.y };
          break;
        default:
          throw new Error(`Direction ${direction} is invalid`);
      }
      this.drawText(canvas, point.text, textPos, {
        rotate: direction === "top" || direction === "bottom" ? 0 : -90
      });
    }
  }

  static drawContentPoints(canvas, points) {
    for (let i = 0, len = points.length; i < len; i++) {
      const point = points[i];
      const horizontalLine = [
        { x: point.x - CONTENT_LINE_LENGTH / 2, y: point.y },
        { x: point.x + CONTENT_LINE_LENGTH / 2, y: point.y }
      ];
      const verticalLine = [
        { x: point.x, y: point.y - CONTENT_LINE_LENGTH / 2 },
        { x: point.x, y: point.y + CONTENT_LINE_LENGTH / 2 }
      ];

      this.drawLine(canvas, horizontalLine[0], horizontalLine[1], {
        strokeStyle: "white",
        lineWidth: 0.5
      });
      this.drawLine(canvas, verticalLine[0], verticalLine[1], {
        strokeStyle: "white",
        lineWidth: 0.5
      });
    }
  }

  static findEndPoint(start, direction, length) {
    let end;
    switch (direction) {
      case "top":
        end = { x: start.x, y: start.y - length };
        break;
      case "bottom":
        end = { x: start.x, y: start.y + length };
        break;
      case "right":
        end = { x: start.x + length, y: start.y };
        break;
      case "left":
        end = { x: start.x - length, y: start.y };
        break;
      default:
        throw new Error(`Direction ${direction} is invalid`);
    }
    return end;
  }

  static drawLine(canvas, from, to, options = {}) {
    const fromPoint = {
      x: from.x + BORDER_LINE_LENGTH + DRAW_LINE_OFFSET,
      y: from.y + BORDER_LINE_LENGTH + DRAW_LINE_OFFSET
    };

    const toPoint = {
      x: to.x + BORDER_LINE_LENGTH + DRAW_LINE_OFFSET,
      y: to.y + BORDER_LINE_LENGTH + DRAW_LINE_OFFSET
    };

    drawLine(canvas, fromPoint, toPoint, options);
  }

  static drawText(canvas, text, position, options = {}) {
    const point = {
      x: position.x + BORDER_LINE_LENGTH + DRAW_TEXT_OFFSET,
      y: position.y + BORDER_LINE_LENGTH + DRAW_TEXT_OFFSET
    };

    drawText(canvas, text, point, options);
  }
}
